/**
 * Daily Output — the vendors' own sheet, as a grid.
 *
 * Built from docs/MS-OUTPUT REPORT _20260905.xlsx and docs/Output Report-TS_NEW 20260903.xlsx, whose
 * common rules were measured on 400 rows of each rather than assumed: the day's figure is the SUM OF
 * HOURLY SLOTS (400/400, both, no exceptions), Total Working Hours is workers x working time, and
 * efficiency is the day's output x cycle time / those hours. DONE QTY in their files is cumulative.
 *
 * THE PROBLEM THIS SCREEN EXISTS TO SOLVE is not data entry. Max, 2026-09-11: "vendor điền tùm lum ko
 * đúng cú pháp nên khá khó để đưa vào phần skin output và case output trong capacity". A typed
 * description never matches a PO line, so the numbers cannot be posted anywhere. So NOTHING
 * identifying is typed here: the item is CHOSEN from this vendor's own open PO lines, and PO#,
 * description, colour, order quantity and cycle time are then the portal's, not the vendor's. The
 * production line is chosen from the ones that vendor has used before.
 *
 * IT OPENS AS A BLANK GRID (Max: "để bảng trống dạng lưới như trong excel"). Empty rows are always
 * waiting at the bottom; picking an item in one turns it into a real row and another blank appears.
 * There is no "add" step to find first.
 *
 * TYPED LIKE A SPREADSHEET. Up to 41 rows a day on MS's busiest, thirteen boxes each. Every box saves
 * on blur, Enter and the arrows move around the grid, and the totals are never typed.
 */
import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation, Trans } from 'react-i18next';
import i18n from './i18n';
import { CellCombo, settlePicks, filtersFor, PICK_FIELDS, RELAX } from './OutputCellPicker';
import {
  RefreshCw, AlertTriangle, Trash2, X, CalendarDays, Check, TriangleAlert,
  ClipboardList, Pencil, Link2, Ban, ShieldCheck,
} from 'lucide-react';
import OutputCheck from './OutputCheck';
import { SortTh, sortRows, rowPassesColumnFilters, distinctColumnValues } from './15_SampleTracker';
import Header from './Header';
import useAuth from './useAuth';
import { BACKEND_URL } from './backendConfig';
import ModuleHelp from './ModuleHelp';
import './SPHub.css';
import './Capacity.css';
import './CapacityOutput.css';

const API = `${BACKEND_URL}/api/production/capacity/output`;

/* EVERY call carries the vendor. Staff are not tied to a factory, so the server has nobody to answer
   for without it and returns an empty list — which reads on screen as "there is nothing here". That
   has now shipped twice from two different call sites, so the query string is built in one place and
   no caller can forget it. */
let currentVendor = '';
const apiUrl = (path, params = {}) => {
  const qs = new URLSearchParams(params);
  if (currentVendor) qs.set('vendor', currentVendor);
  const q = qs.toString();
  return `${API}${path}${q ? `?${q}` : ''}`;
};
/* The sheet opens fifteen rows deep and never runs out: Max, 2026-09-12, "cho sẵn bảng có 15 dòng nếu
   điền xong dòng 15 thì tự động có thêm dòng 16". So the table is at least MIN_ROWS tall, and there is
   always at least one empty row under the last one that has something in it. */
const MIN_ROWS = 15;

/* DECLARED WIDTHS, with a colgroup and table-layout: fixed.
   On an automatic layout every column moves as the content changes — switching from MS to TS shifted
   Line # by 60px, and a value typed into what looked like the line box landed in Workers instead.
   A number input silently drops the letters, so "B3" became 3 and nothing said so. The columns hold
   still now whatever is in them. */
/* The funnel button takes 22px out of a header, so Month and Skin/Case were paying for their filter
   with their own names — "Mo…" and "Skin/…". PO# and Description gave back what they needed. */
const FIXED = [
  ['#', 32], ['Month', 78], ['PO#', 108], ['Description', 160], ['Colour', 96],
  ['Skin/Case', 92], ['Order', 56], ['Line #', 104], ['Workers', 74], ['Hrs', 42],
];
/* Total and Remain hold four- and five-figure numbers with a thousands separator — 20,362 is nine
   characters — and at 54px they rendered as "0…" with the header clipped to "Rem…" (Max, 2026-09-13:
   "tăng width 2 cột này nhé"). The table scrolls sideways anyway, so the width costs nothing that was
   not already being scrolled to. */
// The last column holds one button on a sheet row and up to three on an unmatched one: Attach (with
// its word — it is the one that writes), clear what was chosen, and dealt with. Sized by the widest.
const TAIL = [['Total', 84], ['Remain', 88], ['Eff.', 52], ['·', 128]];
const SLOT_W = 48;

/* MS runs thirteen hourly columns, so this table scrolls sideways however narrow the rest is made —
   their own sheet does too, and freezes its panes at the same place. The first four columns say WHICH
   ROW you are on; without them a scrolled-right grid is a wall of numbers belonging to nothing.
   Offsets are summed from the declared widths rather than measured: with table-layout fixed they are
   the widths, and measuring was only ever needed because Capacity's columns are elastic. */
/* SIX, not four. Colour and Skin/Case say WHICH of a style's rows you are on, and they were the first
   two columns to slide under the frozen block the moment the grid scrolled sideways — Max, 2026-09-12,
   "UI chỗ freeze color bị lỗi": at 1,258px of scroll port the table needs 430px of scrolling, and at
   that point Colour was completely hidden behind Description. Their own workbook freezes its panes
   after Skin/Case for the same reason. PO# and Description gave up 38px between them to pay for it. */
const FROZEN = 6;
const LEFTS = FIXED.slice(0, FROZEN).reduce((acc, [, w], i) => {
  acc.push(i === 0 ? 0 : acc[i - 1] + FIXED[i - 1][1]);
  return acc;
}, []);
/* The class is MERGED, never replaced: spreading this over className="co-dim" dropped co-dim, so the
   frozen cells rendered in a different colour from the ones next to them. */
/* `extra` is how a frozen cell keeps a class of its own. Written as className="co-rowno" BEFORE the
   spread, the spread's className replaced it — so the grey row number and the amber edge on a flagged
   row (`tr.co-gaprow td.co-rowno`) never rendered on any row; found 2026-10-12 when the unmatched rows'
   red edge did not show either. */
const frozenProps = (i, extra = '') => (i < FROZEN
  ? {
    className: `${extra} co-freeze${i === FROZEN - 1 ? ' co-freeze-edge' : ''}`.trim(),
    style: { left: LEFTS[i] },
  }
  : (extra ? { className: extra } : {}));
const blankCount = (used) => Math.max(MIN_ROWS - used, 1);

/* The typed columns of a row, left to right, before the hourly boxes. The arrow keys and a paste out
   of Excel both need to know what sits next to what, and this is the one place that says so. */
const META_COLS = ['lineNo', 'workers', 'workingHours'];
/* Excel hands over "1,234", "12.0 " and the odd stray character. A number box keeps the first digits
   it can read and says nothing, so the number is taken out of the text here instead. */
const cleanNumber = (v) => {
  const m = String(v).replace(/[\s,]/g, '').match(/-?\d+(\.\d+)?/);
  return m ? m[0] : '';
};

const n0 = (v) => (v === null || v === undefined || v === '' ? '' : Number(v).toLocaleString('en-GB', { maximumFractionDigits: 0 }));
const n1 = (v) => (v === null || v === undefined || v === '' ? '' : Number(v).toLocaleString('en-GB', { maximumFractionDigits: 1 }));
/* The factory's calendar day, not UTC's. toISOString() would hand a line in Vietnam yesterday's date
   for the whole of its morning shift — 07:00 local is still the previous day in UTC. 'sv-SE' formats
   as YYYY-MM-DD, which is why the backend's portalTodayVN uses it too. */
const today = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Ho_Chi_Minh' }).format(new Date());

const slotSum = (hours) => Object.values(hours || {})
  .reduce((a, v) => a + (v === null || v === '' || Number.isNaN(Number(v)) ? 0 : Number(v)), 0);

/* ONE rule, no exception — Max, 2026-09-12: "tổng output từng ngày bằng tổng qty các giờ cộng lại".
   The day's figure IS the sum of the hourly boxes. It used to fall back to the stored total on a row
   that had no hours behind it, which made the imported days and the opening balances look finished
   while nothing on the screen could be traced back to an hour. They are no longer folded in silently:
   the total says what the hours say, and the row carries a flag. */
const dayTotal = (e, hours) => slotSum(hours);

/* What is wrong with this row, or null when it obeys the rule. `pending` = the row is being typed in
   right now, so the stored total is simply behind and is not a discrepancy. */
const rowGap = (e, hours, pending) => {
  const sum = slotSum(hours);
  const stored = e.dayQty === null || e.dayQty === undefined ? 0 : Number(e.dayQty);
  const hasHours = pending || e.hoursEntered > 0;
  /* The 50 rows carried over from the Capacity Management workbook are a running total to date parked
     on one date, not that day's production. They can never have hours, so they are named rather than
     left looking like a day somebody forgot to fill in. */
  const opening = /opening balance/i.test(e.note || '');
  if (!hasHours) return stored > 0 ? { kind: 'nohours', stored, sum: 0, opening } : null;
  if (!pending && Math.abs(stored - sum) >= 0.5) return { kind: 'diff', stored, sum, opening };
  return null;
};
const gapText = (g, t) => {
  if (g.kind === 'diff') return t('xOutput.gapDiff', { stored: n0(g.stored), sum: n0(g.sum) });
  if (g.opening) return t('xOutput.gapOpening', { stored: n0(g.stored) });
  return t('xOutput.gapNoHours', { stored: n0(g.stored) });
};
/* ---- the Excel column menu, for all three tables on this screen -------------------------------
   Max, 2026-09-12: "thêm filter như excel cho cả 3 phần". This is the portal's own funnel — the one
   the tracker, Capacity and Product Line use — so a column behaves the same wherever it is met:
   click the label to sort, the funnel for a searchable tick list of the values actually present. */
function useColumnMenu(getters) {
  const [sort, setSort] = useState({ key: '', dir: 'asc' });
  const [colFilters, setColFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);

  /* `rows` is the list BEFORE this screen's column filters. The menu then lists every value the
     column really holds, minus what the OTHER columns have already ruled out. */
  const thProps = (key, rows) => ({
    sortKey: key,
    sort,
    onSort: (k, dir) => setSort((prev) => (dir
      ? { key: k, dir }
      : (prev.key === k ? { key: k, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'asc' }))),
    filterValues: openFilter?.key === key ? distinctColumnValues(rows, key, colFilters, getters) : undefined,
    filterSelected: colFilters[key],
    filterOpen: openFilter?.key === key,
    filterAnchor: openFilter?.key === key ? openFilter.anchor : null,
    onOpenFilter: (k, anchor) => setOpenFilter((prev) => (prev && prev.key === k ? null : { key: k, anchor })),
    onCloseFilter: () => setOpenFilter(null),
    onApplyFilter: (k, values, allValues) => {
      setColFilters((prev) => {
        const next = { ...prev };
        // Ticking everything is not a filter; storing it would leave a funnel on a column hiding nothing.
        const isAll = allValues && allValues.length && values.length >= allValues.length;
        if (!values.length || isAll) delete next[k]; else next[k] = values;
        return next;
      });
      setOpenFilter(null);
    },
  });

  const apply = (rows) => {
    const kept = Object.keys(colFilters).length
      ? rows.filter((r) => rowPassesColumnFilters(r, colFilters, getters))
      : rows;
    return sort.key ? sortRows(kept, sort, getters) : kept;
  };
  const clear = () => { setColFilters({}); setSort({ key: '', dir: 'asc' }); };
  return {
    thProps, apply, clear, sort,
    filtered: Object.keys(colFilters).length,
    active: Object.keys(colFilters).length > 0 || !!sort.key,
  };
}

/* A column filters and sorts on the value the screen SHOWS — the description falls back to the SAP
   code exactly as the cell does, or a funnel would offer a name that is nowhere on screen. */
const GRID_GETTERS = {
  shipMonth: (e) => e.shipMonth || '',
  poLabel: (e) => e.poLabel || '',
  description: (e) => e.description || e.sapCode || '',
  colour: (e) => e.colour || '',
  part: (e) => e.part || '',
  lineNo: (e) => e.lineNo || '',
};
const SHEET_GETTERS = {
  outputDate: (d) => d.date,
  rowsN: (d) => d.rows,
  qty: (d) => d.qty,
  workHours: (d) => d.workHours,
  linesN: (d) => d.lines,
  // Read in the reader's language when the funnel asks (render time), so it offers the words on screen.
  check: (d) => [
    d.diffRows ? i18n.t('xOutput.chkNotAddUp') : '',
    d.gapRows ? (d.openRows === d.gapRows ? i18n.t('xOutput.chkOpening') : i18n.t('xOutput.chkNoHourly')) : '',
    d.unmatched ? i18n.t('xOutput.chkUnmatched') : '',
  ].filter(Boolean).join(' · '),
  gapDays: (d) => (d.gapDays ? i18n.t('xOutput.daysWithNothing', { count: d.gapDays }) : ''),
  lastBy: (d) => d.lastBy || '',
};
const UN_GETTERS = {
  vendorCode: (g) => g.vendorCode || '',
  description: (g) => g.description || '',
  colour: (g) => g.colour || '',
  part: (g) => g.part || '',
  reason: (g) => g.reason || '',
  qty: (g) => g.qty,
  days: (g) => g.days,
  lastDay: (g) => g.lastDay || '',
};

const workingHours = (e) => (e.workers && e.workingHours ? e.workers * e.workingHours : null);
const efficiency = (e, total) => {
  const twh = workingHours(e);
  if (!twh || !e.cycleTime) return null;
  return Math.round((total * e.cycleTime) / twh * 1000) / 10;
};

/* Where the bands sit, in one place with the reasoning: 100% means the line produced exactly the hours
   it was paid for, so under 85% is worth a look and over 100% is worth checking the cycle time. */
const effTone = (pct) => {
  if (pct === null) return '';
  if (pct >= 100) return 'co-high';
  if (pct < 85) return 'co-low';
  return 'co-ok';
};

/**
 * The cascade: Month > PO# > Description > Colour > Sewing type.
 *
 * Max, 2026-09-12: the vendor picks these in order, each one narrowing the next, with Skin/Case moved
 * to after Colour. That is the order and the wording of columns C-F of their own sheet.
 *
 * Every list comes from the server, from the master data behind this vendor's PO lines — nothing here
 * is typed, which is the whole reason the screen exists. A step with exactly one answer fills itself
 * and moves on, so a vendor with one cycle running does not click through four menus to say so.
 */
const STEPS = [
  { key: 'month', labelKey: 'xOutput.stepMonth' },
  { key: 'po', labelKey: 'xOutput.colPo' },
  { key: 'description', labelKey: 'xOutput.colDescription' },
  { key: 'colour', labelKey: 'xOutput.colColour' },
  { key: 'part', labelKey: 'xOutput.colSkinCase' },
];

function Cascade({ onPick, onClose, seed, anchor }) {
  const { t } = useTranslation();
  /* Seeded from the row above: somebody entering a shift works through one cycle at a time, and
     starting every row at Month again is the friction that sends people back to the spreadsheet. */
  const [chosen, setChosen] = useState(seed || {});
  const [step, setStep] = useState(() => STEPS.findIndex((s) => !(seed || {})[s.key]) === -1
    ? 0 : STEPS.findIndex((s) => !(seed || {})[s.key]));
  /* The options are stored WITH the step they belong to. Keeping them in a bare array let a step that
     auto-advanced render the previous step's values in the next step's shape — the panel read
     `o.remainQty` off a colour name and the whole thing threw. */
  const [options, setOptions] = useState({ field: null, list: [] });
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState('');

  const field = STEPS[step]?.key;

  useEffect(() => {
    let dead = false;
    if (!field) return undefined;
    setBusy(true); setErr('');
    setOptions({ field: null, list: [] });
    (async () => {
      try {
        const params = { field };
        STEPS.slice(0, step).forEach((st) => { if (chosen[st.key]) params[st.key] = chosen[st.key]; });
        const res = await fetch(apiUrl('/options', params), { credentials: 'include' });
        const data = await res.json();
        if (dead) return;
        if (!res.ok) throw new Error(data.error || i18n.t('xOutput.failed', { status: res.status }));
        const opts = data.options || [];
        setOptions({ field, list: opts });
        /* One answer is not a choice. Take it and move on — except on the last step, where taking it
           would create a row nobody asked for. */
        if (opts.length === 1 && field !== 'part') {
          setChosen((p) => ({ ...p, [field]: opts[0].value }));
          setStep((n) => n + 1);
        }
      } catch (e) {
        if (!dead) setErr(e.message);
      } finally {
        if (!dead) setBusy(false);
      }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field, step, JSON.stringify(chosen)]);

  const take = (value) => {
    if (field === 'part') { onPick(value); return; }
    setChosen((p) => ({ ...p, [field]: value }));
    setStep((n) => n + 1);
  };
  const backTo = (i) => {
    const next = { ...chosen };
    STEPS.slice(i).forEach((st) => delete next[st.key]);
    setChosen(next);
    setStep(i);
  };

  /* POSITIONED FIXED, from the button's own rectangle.
     The panel lives inside a table cell, and every cell on this table carries `overflow: hidden` and
     is 34px tall — an absolutely positioned 320px panel inside one is clipped to nothing. It opened,
     it was in the DOM, it reported visible, and it painted nothing: exactly the "bấm vào không có gì
     xảy ra" Max hit. Fixed positioning escapes the cell's clip and the table's scroll box, and lets
     the panel flip above the row when it is near the bottom of the screen. */
  const W = 460; const H = 320;
  const pos = {};
  if (anchor) {
    pos.left = Math.max(8, Math.min(anchor.left, window.innerWidth - W - 8));
    pos.top = anchor.bottom + H + 8 > window.innerHeight
      ? Math.max(8, anchor.top - H - 4)
      : anchor.bottom + 2;
  }

  /* RENDERED INTO document.body, not into the cell it belongs to.
     Fixed positioning got it out of the cell's 34px clip, but the panel was still a CHILD of a sticky
     table cell, so it painted inside that cell's stacking context: the frozen Description column, a
     later sibling with the same z-index, covered it and only the numbers down its right edge showed.
     A portal takes it out of the table altogether, where its own z-index is the one that counts. */
  return createPortal(
    <>
      <div className="co-pick-back" onClick={onClose} />
      <div className="co-pick" style={pos}>
        <div className="co-pick-head">
          <span className="co-pick-title">{STEPS[step] ? t(STEPS[step].labelKey) : t('xOutput.choose')}</span>
          <button type="button" className="sp-btn tiny" onClick={onClose}><X size={11} /></button>
        </div>
        {/* What has been chosen so far, each one a way back to that step. */}
        {step > 0 && (
          <div className="co-crumbs">
            {STEPS.slice(0, step).map((st, i) => (
              <button key={st.key} type="button" className="co-crumb" onClick={() => backTo(i)}>
                {chosen[st.key]}
              </button>
            ))}
          </div>
        )}
        {!!err && <div className="sp-error"><AlertTriangle size={12} /> {err}</div>}
        <div className="co-pick-body">
          {(busy || options.field !== field) && <div className="co-pick-empty">{t('xOutput.loading')}</div>}
          {!busy && options.field === field && !options.list.length
            && <div className="co-pick-empty">{t('xOutput.nothingLeft')}</div>}
          {!busy && options.field === field && field !== 'part' && options.list.map((o) => (
            <button key={o.value} type="button" className="co-opt" onClick={() => take(o.value)}>
              <span>{o.label || t('xOutput.blank')}</span>
              <em>{o.n}</em>
            </button>
          ))}
          {!busy && options.field === field && field === 'part' && options.list.map((o) => (
            <button key={o.part} type="button" className="co-opt co-opt-part" onClick={() => take(o)}>
              <span>{o.part}</span>
              <em>
                {t('xOutput.leftOf', { left: n0(o.remainQty), order: n0(o.orderQty) })}
                {o.lines > 1 ? ` · ${t('xOutput.destinations', { n: o.lines })}` : ''}
              </em>
            </button>
          ))}
        </div>
      </div>
    </>,
    document.body,
  );
}

/* What the vendors' files said that could not be attached to an order.
   Grouped by item + colour + reason: nearly two thousand individual rows is a list nobody reads, sixty
   item names is a morning's work. Marking one done does not import it — it means somebody has dealt
   with the name, and the next load will pick it up. */
function Unmatched({ onClose, canResolve }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(true);
  const menu = useColumnMenu(UN_GETTERS);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const res = await fetch(apiUrl('/unmatched'), { credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xOutput.failed', { status: res.status }));
      setData(d); setErr('');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (g) => {
    try {
      const res = await fetch(apiUrl('/unmatched/resolve'), {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vendorCode: g.vendorCode, description: g.description,
          colour: g.colour, reason: g.reason }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || t('xOutput.failed', { status: res.status }));
      await load();
    } catch (e) { setErr(e.message); }
  };

  const groups = data?.groups || [];
  const shown = menu.apply(groups);
  const th = (key) => menu.thProps(key, groups);

  return (
    <div className="co-unmatched">
      <div className="co-un-head">
        <TriangleAlert size={14} />
        <strong>{t('xOutput.unTitle')}</strong>
        {data && (
          <em>
            {t('xOutput.unRows', { total: data.total.toLocaleString('en-GB') })} · {menu.active
              ? t('xOutput.unItemsOf', { shown: shown.length, n: data.groupTotal })
              : t('xOutput.unItems', { n: data.groupTotal })}
            {data.groupTotal > groups.length && ` ${t('xOutput.unLargest', { n: groups.length })}`}
          </em>
        )}
        {menu.active && (
          <button type="button" className="sp-btn tiny" title={t('xOutput.clearFilterTitle')} onClick={menu.clear}>
            <X size={12} /> {t('xOutput.filteredBtn')}
          </button>
        )}
        <button type="button" className="sp-btn tiny" onClick={onClose}><X size={12} /></button>
      </div>
      <p className="co-un-note">{t('xOutput.unNote')}</p>
      {!!err && <div className="sp-error"><AlertTriangle size={13} /> {err}</div>}
      {busy && <div className="co-pick-empty">{t('xOutput.loading')}</div>}
      {!busy && data && !groups.length && (
        <div className="co-pick-empty">{t('xOutput.nothingUnmatched')}</div>
      )}
      {/* A table rather than a list of cards, so every column carries the same funnel as the other
          two tables on this screen: the way through 964 names is "show me TS, reason X", not scrolling. */}
      {!busy && data && !!shown.length && (
        <div className="co-un-body">
          <table className="co-sheet-table co-un-table">
            <thead>
              <tr>
                <SortTh label={t('xOutput.colVendor')} {...th('vendorCode')} />
                <SortTh label={t('xOutput.colFileDescription')} {...th('description')} />
                <SortTh label={t('xOutput.colColour')} {...th('colour')} />
                <SortTh label={t('xOutput.colSkinCase')} {...th('part')} />
                <SortTh label={t('xOutput.colWhy')} {...th('reason')} />
                <SortTh label={t('xOutput.colPcs')} className="cap-num" {...th('qty')} />
                <SortTh label={t('xOutput.colDays')} className="cap-num" {...th('days')} />
                <SortTh label={t('xOutput.colLastSeen')} {...th('lastDay')} />
                {canResolve && <th className="cap-act">·</th>}
              </tr>
            </thead>
            <tbody>
              {shown.map((g) => (
                <tr key={`${g.vendorCode}|${g.description}|${g.colour}|${g.part}|${g.reason}`}>
                  <td><span className="co-login-v">{g.vendorCode}</span></td>
                  <td title={g.description}><strong>{g.description}</strong></td>
                  <td>{g.colour}</td>
                  <td>{g.part}</td>
                  <td className="co-dim" title={g.reason}>{g.reason}</td>
                  <td className="cap-num"><strong>{n0(g.qty)}</strong></td>
                  <td className="cap-num">{g.days}</td>
                  <td className="co-dim">{g.lastDay}</td>
                  {canResolve && (
                    <td className="cap-act">
                      <button type="button" className="sp-btn tiny" title={t('xOutput.markDealtWith')} onClick={() => resolve(g)}>
                        <Check size={12} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!busy && data && !!groups.length && !shown.length && (
        <div className="co-pick-empty">{t('xOutput.nothingMatchesFilter')}</div>
      )}
    </div>
  );
}

/* WHAT MAKES A DAY WORTH GOING BACK TO. Used by the toolbar's count and by the list's own filter, so
   a badge that says 12 cannot open a list of 9.

   A row whose stored total is not what its hour boxes add up to (`diffRows`) is the real mismatch. A
   row with no hourly detail counts too — unless every one of them is an opening balance carried over
   from the Capacity Management file, which is a known state and not somebody's mistake. */
const dayNeedsCheck = (d) => (d.diffRows || 0) > 0
  || ((d.gapRows || 0) - (d.openRows || 0)) > 0
  /* And the rows the import could not attach to an order — 4,097 of them across 400 days, which is
     what Max was actually looking at: they were only ever visible by opening the Unmatched panel and
     reading dates out of it. */
  || (d.unmatched || 0) > 0;

/* THE LIST OF SHEETS. One day is one sheet, and this is which ones exist.
   Max, 2026-09-12: "mỗi ngày là 1 phiếu thì tôi có thể coi xem hiện vendor đã điền những ngày nào".
   A calendar answered that a month at a time, which only helps if you already know which month to look
   in — the question is nearly always "has anything come in lately", so this is a plain list, newest
   first, across every month the vendor has ever reported.

   Days with nothing in them are not rows here at all. A sheet that does not exist has no line in a
   list of sheets, and a gap between two dates is the thing worth seeing. */
function SheetList({ onPickDay, current, today, data, err, busy, onReload, issuesOnly, onIssuesOnly }) {
  const { t } = useTranslation();
  const menu = useColumnMenu(SHEET_GETTERS);

  /* The gap since the sheet before it — the column somebody chasing a vendor actually reads. Worked
     out on the SERVER's order (newest first) and carried on the row: once the list is sorted or
     filtered, the row above is no longer the sheet before. */
  const source = data?.days || [];
  const days = source.map((d, i) => {
    const prev = source[i + 1];
    if (!prev) return { ...d, gapDays: 0 };
    const n = Math.round((new Date(d.date) - new Date(prev.date)) / 86400000);
    return { ...d, gapDays: n > 1 ? n - 1 : 0 };
  });
  /* "Only the days that need checking" — Max, 2026-09-13: "tôi thấy nhiều cái chưa khớp cần chỉnh
     lại mà". Reads the same predicate the toolbar counts with, so the button and the list can never
     disagree about what a day needing attention is. */
  const pool = issuesOnly ? days.filter(dayNeedsCheck) : days;
  const shown = menu.apply(pool);
  const th = (key) => menu.thProps(key, pool);

  return (
    <div className="co-sheets">
      <div className="co-sheets-head">
        <ClipboardList size={14} />
        <strong>{t('xOutput.sheetsTitle')}</strong>
        {data && (
          <em>
            {menu.active ? `${shown.length} / ` : ''}{t('xOutput.sheetDays', { count: days.length })}
            {data.months?.length ? ` · ${t('xOutput.sheetMonths', { count: data.months.length })}` : ''}
          </em>
        )}
        {menu.active && (
          <button type="button" className="sp-btn tiny" title={t('xOutput.clearFilterTitle')} onClick={menu.clear}>
            <X size={12} />
          </button>
        )}
        {/* "Only the days that need checking" — Max, 2026-09-13: "tôi thấy nhiều cái chưa khớp cần
            chỉnh lại mà". The same predicate the toolbar counts with, so a badge saying 12 can never
            open a list of 9. */}
        <button
          type="button"
          className={`sp-btn tiny ${issuesOnly ? 'cap-on' : ''}`}
          title={t('xOutput.issuesTitle')}
          onClick={() => onIssuesOnly(!issuesOnly)}
        >
          <TriangleAlert size={11} />
          {issuesOnly ? t('xOutput.issuesOnlyOn') : t('xOutput.issuesCount', { n: days.filter(dayNeedsCheck).length })}
        </button>
        <button type="button" className="sp-btn tiny" onClick={onReload} disabled={busy}>
          <RefreshCw size={12} />
        </button>
      </div>
      {!!err && <div className="sp-error"><AlertTriangle size={13} /> {err}</div>}
      {busy && !data && <div className="co-pick-empty">{t('xOutput.loading')}</div>}
      {!busy && !days.length && <div className="co-pick-empty">{t('xOutput.noSheets')}</div>}
      {!!days.length && (
      <div className="co-sheets-body">
        <table className="co-sheet-table">
          <thead>
            <tr>
              <SortTh label={t('xOutput.colDate')} {...th('outputDate')} />
              <SortTh label={t('xOutput.colRows')} className="cap-num" {...th('rowsN')} />
              <SortTh label={t('xOutput.colOutput')} className="cap-num" {...th('qty')} />
              <SortTh label={t('xOutput.colWorkHours')} className="cap-num" {...th('workHours')} />
              <SortTh label={t('xOutput.colLines')} className="cap-num" {...th('linesN')} />
              <SortTh label={t('xOutput.colNeedsChecking')} {...th('check')} />
              <SortTh label={t('xOutput.colSinceBefore')} {...th('gapDays')} />
              <SortTh label={t('xOutput.colEnteredBy')} {...th('lastBy')} />
            </tr>
          </thead>
          <tbody>
            {shown.map((d) => {
              const gap = d.gapDays;
              return (
                <tr
                  key={d.date}
                  className={`${d.date === current ? 'on' : ''} ${d.date === today ? 'today' : ''}`}
                  onClick={() => onPickDay(d.date)}
                >
                  <td><strong>{d.date}</strong>{d.date === today && <em> · {t('xOutput.todayTag')}</em>}</td>
                  <td className="cap-num">{d.rows}</td>
                  <td className="cap-num"><strong>{n0(d.qty)}</strong></td>
                  <td className="cap-num">{d.workHours ? n0(d.workHours) : ''}</td>
                  <td className="cap-num">{d.lines || ''}</td>
                  {/* The note Max asked for: which sheets hold a number the hours cannot account for. */}
                  <td className="co-sheet-flag">
                    {d.gapRows > 0 && (
                      <span
                        className="co-gapmark"
                        title={t('xOutput.gapMarkTitle', { rows: d.gapRows, qty: n0(d.gapQty) })
                          + (d.openRows === d.gapRows ? `\n${t('xOutput.gapMarkAllOpening')}` : '')}
                      >
                        <TriangleAlert size={11} />
                        {d.openRows === d.gapRows
                          ? t('xOutput.gapMarkOpening', { count: d.gapRows, qty: n0(d.gapQty) })
                          : t('xOutput.gapMarkNoHourly', { count: d.gapRows, qty: n0(d.gapQty) })}
                      </span>
                    )}
                    {d.diffRows > 0 && (
                      <span className="co-gapmark" title={t('xOutput.diffTitle', { n: d.diffRows })}>
                        <TriangleAlert size={11} />{t('xOutput.diffText', { count: d.diffRows })}
                      </span>
                    )}
                    {d.unmatched > 0 && (
                      <span
                        className="co-gapmark co-unmark"
                        title={t('xOutput.unmarkTitle', { n: d.unmatched, qty: n0(d.unmatchedQty) })}
                      >
                        <TriangleAlert size={11} />{t('xOutput.unmarkText', { n: d.unmatched, qty: n0(d.unmatchedQty) })}
                      </span>
                    )}
                  </td>
                  <td className={gap ? 'co-gap' : ''}>{gap ? t('xOutput.daysWithNothing', { count: gap }) : ''}</td>
                  <td className="co-dim">{d.lastBy || ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

/* THE GUIDE (Max 01/11: every module has one). The vendor picker is drawn only for people who are not tied to one vendor,
   and everything under it only once a vendor is chosen, so TourGuide skips the steps that are not on the screen. */
const TOUR = [
  { selector: '.co-vendorbar', title: 'tourOutput.vendorT', body: 'tourOutput.vendorB' },
  { selector: '.co-datewrap', title: 'tourOutput.dayT', body: 'tourOutput.dayB' },
  { selector: '.co-sum', title: 'tourOutput.sumT', body: 'tourOutput.sumB' },
  { selector: '.co-daybar .cap-bar-right', title: 'tourOutput.toolsT', body: 'tourOutput.toolsB' },
  { selector: 'table.co-table', title: 'tourOutput.gridT', body: 'tourOutput.gridB' },
];

export default function CapacityOutput() {
  const { t } = useTranslation();
  const { permissions } = useAuth();
  const mayEdit = (permissions || {}).capacity_vendor === 'edit'
    || (permissions || {}).capacity === 'edit';

  const [date, setDate] = useState(today);
  const [vendorCode, setVendorCode] = useState(null);
  const [locked, setLocked] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [pickVendor, setPickVendor] = useState('');
  const [slots, setSlots] = useState([]);
  const [lineNames, setLineNames] = useState([]);
  const [entries, setEntries] = useState([]);
  /* The rows of this day's file that the import could not attach to an order. Drawn under the sheet's
     own rows, in their own colour — Max, 2026-10-12: "những cái nào ko match cũng hiện ở dưới luôn và
     đánh dấu màu khác để tôi biết tôi chỉnh á". Not in the day's total: nothing has been attached. */
  const [unmatchedRows, setUnmatchedRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingIds, setSavingIds] = useState([]);
  // Which blank row has its picker open.
  const [pickingAt, setPickingAt] = useState(null);
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [showCal, setShowCal] = useState(true);
  // The data check (Jay via Max, 2026-10-18) — shut until asked for; it reads 30 days of rows.
  const [showCheck, setShowCheck] = useState(false);
  /* The list of sheets is loaded HERE, not inside the panel: the toolbar has to count the days that
     need checking whether the panel is open or not, and reloading it after a save is how the count
     stops being stale the moment somebody fixes a day. */
  const [sheets, setSheets] = useState(null);
  const [sheetsErr, setSheetsErr] = useState('');
  const [sheetsBusy, setSheetsBusy] = useState(true);
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [draft, setDraft] = useState({});
  /* "Sửa cả bảng": while it is on, nothing is written when a box is left — the line, workers and
     hours boxes only remember, and one button writes the whole table. Max, 2026-09-12: "tôi cần nút
     edit toàn bảng và khi save sẽ cho cả bản". */
  const [bulk, setBulk] = useState(false);
  /* THE TABLE IS READ-ONLY UNTIL SOMEBODY ASKS TO EDIT IT — Max, 2026-09-13: "phải bấm sửa bảng thì
     mới sửa dc". It used to write on every blur, which meant a click into the wrong box and a stray
     keystroke changed a day's output with nothing to undo it. One rule now: look at it, or unlock it
     and save it. `mayEdit` is the permission, `editing` is whether the lock is off. */
  const editing = mayEdit && bulk;
  const [metaDraft, setMetaDraft] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const gridRef = useRef(null);
  const gridMenu = useColumnMenu(GRID_GETTERS);
  // The menu lists what the column really holds, so it is fed the day's rows, not the filtered ones.
  /* The unmatched rows are shaped like grid rows (their PO# is the vendor's own text, and they have no
     shipment month) so one funnel filters both — "show me line 9" means the whole of line 9. */
  const unGrid = useMemo(() => unmatchedRows.map((u) => ({
    ...u, isUnmatched: true, shipMonth: '', poLabel: u.poText || '',
  })), [unmatchedRows]);
  const gridTh = (key) => gridMenu.thProps(key, [...entries, ...unGrid]);

  const loadSheets = useCallback(async () => {
    setSheetsBusy(true);
    try {
      const res = await fetch(apiUrl('/month', { withUnmatched: 1 }), { credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xOutput.failed', { status: res.status }));
      setSheets(d); setSheetsErr('');
    } catch (e) { setSheetsErr(e.message); } finally { setSheetsBusy(false); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      currentVendor = pickVendor;
      const res = await fetch(apiUrl('/day', { date }), { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || i18n.t('xOutput.loadFailed', { status: res.status }));
      setVendorCode(data.vendorCode);
      // A vendor login is told which factory it is; keep it for every later call.
      if (data.locked && data.vendorCode) currentVendor = data.vendorCode;
      setLocked(data.locked);
      setVendors(data.vendors || []);
      setSlots(data.slots || []);
      setLineNames(data.lineNames || []);
      setEntries(data.entries || []);
      setUnmatchedRows(data.unmatched || []);
      setDraft({});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [date, pickVendor]);
  useEffect(() => { load(); }, [load]);
  // Re-read the sheet list whenever the vendor changes — it is one query and it is per vendor.
  useEffect(() => { loadSheets(); }, [loadSheets, pickVendor]);

  /* Every earlier day with something wrong in it. TODAY is left out of the count on purpose: the day
     being typed is unfinished by definition, and counting it would make the badge permanent. */
  const needCheck = useMemo(() => {
    const all = (sheets?.days || []).filter(dayNeedsCheck);
    return { all, earlier: all.filter((d) => d.date !== date) };
  }, [sheets, date]);

  const valueOf = (e, slot) => {
    const key = `${e.id}|${slot}`;
    if (key in draft) return draft[key];
    const v = (e.hours || {})[slot];
    return v === null || v === undefined ? '' : String(v);
  };
  const hoursOf = (e) => Object.fromEntries(slots.map((s) => {
    const v = valueOf(e, s);
    return [s, v === '' ? null : Number(v)];
  }));

  /* ---- saving the hours ------------------------------------------------------------------------
     A save REPLACES every slot on the row, because a cleared box has to mean "nothing made in that
     hour" and a patch cannot say that. Fired straight from each box's blur, four quick entries became
     four overlapping full-row writes, and whichever reply landed last won: 20, 23, 26, 29 typed left
     46 in the database while the screen showed 98. The screen was not lying — it was reading its own
     pending values, which is exactly why this had to be caught against the database.

     So hours are written ONE REQUEST AT A TIME per row, and each one sends the row's CURRENT state
     read from a ref rather than whatever the blur handler's closure was built with. A box typed while
     a request is in flight sets the row dirty and another save follows. */
  const draftRef = useRef(draft);
  useEffect(() => { draftRef.current = draft; }, [draft]);
  const entriesRef = useRef(entries);
  useEffect(() => { entriesRef.current = entries; }, [entries]);
  const inFlight = useRef({});
  const dirty = useRef({});

  const liveHours = (id) => {
    const e = entriesRef.current.find((x) => x.id === id) || { hours: {} };
    return Object.fromEntries(slots.map((s) => {
      const k = `${id}|${s}`;
      const raw = k in draftRef.current ? draftRef.current[k] : (e.hours || {})[s];
      return [s, raw === '' || raw === null || raw === undefined ? null : Number(raw)];
    }));
  };

  const saveHours = async (id) => {
    if (inFlight.current[id]) { dirty.current[id] = true; return; }
    inFlight.current[id] = true;
    setSavingIds((p) => [...p, id]);
    setError('');
    const sent = liveHours(id);
    try {
      const res = await fetch(`${API}/${id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hours: sent }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xOutput.saveFailed', { status: res.status }));
      setEntries((prev) => prev.map((x) => (x.id === data.entry.id ? { ...x, ...data.entry } : x)));
      setDraft((p) => {
        const next = { ...p };
        Object.entries(sent).forEach(([slot, v]) => {
          const k = `${id}|${slot}`;
          const shown = next[k];
          // Still what was sent? The server holds it now. Changed since? A later save carries it.
          if (shown !== undefined && (shown === '' ? null : Number(shown)) === v) delete next[k];
        });
        return next;
      });
    } catch (err) {
      setError(err.message);
    } finally {
      inFlight.current[id] = false;
      setSavingIds((p) => p.filter((x) => x !== id));
      if (dirty.current[id]) { dirty.current[id] = false; saveHours(id); }
    }
  };

  const metaValue = (e, key) => {
    const k = `${e.id}|${key}`;
    if (k in metaDraft) return metaDraft[k];
    return e[key] === null || e[key] === undefined ? '' : String(e[key]);
  };
  const setMetaValue = (id, key, value) => setMetaDraft((prev) => ({ ...prev, [`${id}|${key}`]: value }));

  /* The rows carrying something unsaved. Hours live in `draft`, the other three boxes in `metaDraft`;
     both are keyed "<id>|<what>", so the row is the part before the bar. */
  const dirtyIds = () => {
    const set = new Set();
    Object.keys(metaDraft).forEach((k) => set.add(Number(k.split('|')[0])));
    Object.keys(draft).forEach((k) => set.add(Number(k.split('|')[0])));
    return set;
  };
  const dirtyCount = bulk ? dirtyIds().size : 0;

  const save = async (e, patch) => {
    setSavingIds((p) => [...p, e.id]);
    setError('');
    /* What this request is carrying, remembered so that only THESE values are cleared when it
       answers. Clearing every pending box for the row instead threw away anything typed while the
       request was in flight — four boxes filled quickly left the row showing one of them. */
    const sent = patch.hours || {};
    try {
      const res = await fetch(`${API}/${e.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xOutput.saveFailed', { status: res.status }));
      /* MERGED, not replaced. A save answers with what a save can change — the hours, the totals, the
         remaining quantity — and deliberately not with the description, colour or order quantity,
         because fetching those means the view that costs 2.4 seconds a call. The row on screen keeps
         what it already knew. */
      setEntries((prev) => prev.map((x) => (x.id === data.entry.id ? { ...x, ...data.entry } : x)));
      setDraft((p) => {
        const next = { ...p };
        Object.entries(sent).forEach(([slot, v]) => {
          const k = `${e.id}|${slot}`;
          // Still the value that was sent? Then the server now holds it and the draft is spent.
          // Changed since? Leave it — a later save is carrying it.
          const shown = next[k];
          if (shown !== undefined && (shown === '' ? null : Number(shown)) === v) delete next[k];
        });
        return next;
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingIds((p) => p.filter((x) => x !== e.id));
    }
  };

  /* A new row inherits the line the row above it is on. Somebody filling in a shift stays on one line
     for several items, and it is a cell they can change afterwards — nothing is blocked on it. */
  const inheritedLine = () => (entries.length ? entries[entries.length - 1].lineNo : '') || '';

  const pickInto = async (bi, chosen) => {
    const lineNo = inheritedLine();
    setError('');
    /* Adding a row asks the server for it and reloads the day, which would throw away anything typed
       and not yet saved. So the typing goes in FIRST — that is what somebody means by adding a row
       half way through filling a sheet, and asking them to choose between the two would be rude. */
    if (editing && dirtyCount) {
      const ok = await saveAll({ keepEditing: true });
      if (!ok) { setPickingAt(null); return false; }
    }
    try {
      const res = await fetch(API, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ capacityLineId: chosen.lineId, date, part: chosen.part, lineNo }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xOutput.couldNotAdd', { status: res.status }));
      setPickingAt(null);
      const wasEditing = editing;
      await load();
      // `load()` clears the drafts; the lock stays off so the new row can be filled in straight away.
      if (wasEditing) setBulk(true);
      return true;
    } catch (err) {
      setError(err.message);
      setPickingAt(null);
      return false;
    }
  };

  /* Re-point an existing row at a different item. The hours, workers and line stay where they are —
     the only thing that moves is what the row is FOR. */
  const repoint = async (e, chosen) => {
    setError('');
    try {
      const res = await fetch(`${API}/${e.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ capacityLineId: chosen.lineId, part: chosen.part }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xOutput.couldNotChangeItem', { status: res.status }));
      setEntries((prev) => prev.map((x) => (x.id === data.entry.id ? { ...x, ...data.entry } : x)));
      setPickingAt(null);
      return true;
    } catch (err) {
      setError(err.message);
      setPickingAt(null);
      return false;
    }
  };

  /* ATTACH AN UNMATCHED ROW to the order chosen for it. The server turns it into a Daily Output row
     with its production line, workers and every hour box, and marks it dealt with — one transaction,
     so the day is never counted twice or lost. Anything typed and not yet saved goes in first, as
     pickInto does, because the reload that follows would throw it away. */
  const attachUnmatched = async (u, chosen) => {
    setError('');
    if (editing && dirtyCount) {
      const ok = await saveAll({ keepEditing: true });
      if (!ok) { setPickingAt(null); return false; }
    }
    try {
      const res = await fetch(`${API}/unmatched/${u.id}/attach`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ capacityLineId: chosen.lineId, part: chosen.part }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xOutput.couldNotAttach', { status: res.status }));
      setPickingAt(null);
      const wasEditing = editing;
      await load();
      loadSheets();
      if (wasEditing) setBulk(true);
      return true;
    } catch (err) {
      setError(err.message);
      setPickingAt(null);
      return false;
    }
  };

  /* ONE CELL AT A TIME — Max, 2026-10-12 (see OutputCellPicker.jsx). What each blank or unmatched row
     has chosen so far, keyed by row; Skin/Case finishes it. A failed finish keeps what was chosen. */
  const [picks, setPicks] = useState({});
  // The day on screen goes with every list: an order shipping before its month is never offered (Max, 2026-10-13).
  const fetchOptions = useCallback(async (field, filters) => {
    const res = await fetch(apiUrl('/options', { field, ...filters, ...(date ? { day: date } : {}) }), { credentials: 'include' });
    const d = await res.json();
    if (!res.ok) throw new Error(d.error || i18n.t('xOutput.failed', { status: res.status }));
    return d.options || [];
  }, [date]);
  const pickCell = async (rowKey, field, value, finish, base, onSettled, how) => {
    if (field === 'part') {
      const ok = await finish(value);
      if (ok) setPicks((p) => { const n = { ...p }; delete n[rowKey]; return n; });
      return;
    }
    // Picking a cell makes it the person's own, so it stops wearing the guess tint. A Skin/Case the
    // screen filled in from the vendor's file belonged to the cells it was looked up for, so it goes
    // with them and is looked up again once the four are set (readyFor below).
    const prev = picks[rowKey] || base || {};
    const next = {
      ...prev, [field]: value, _guess: { ...(prev._guess || {}), [field]: undefined },
      ...(prev._readyFor !== undefined ? { part: undefined, _ready: undefined, _readyFor: undefined } : {}),
    };
    // One of the item's OTHER choices (RELAX): the cells it was not narrowed by are cleared, and the
    // settle below fills each back where only one answer is left.
    if (how && how.wider) {
      PICK_FIELDS.filter((f) => f !== field && !RELAX[field].includes(f)).forEach((f) => { next[f] = undefined; });
    }
    // An emptied cell stays empty through this settle (it would otherwise fill itself straight back).
    const hold = how && how.clear ? [field] : [];
    setPicks((p) => ({ ...p, [rowKey]: next }));
    const settled = await settlePicks(next, fetchOptions, hold);
    // Only if nothing was picked on this row meanwhile — a later pick has its own settle coming.
    let current = false;
    setPicks((p) => {
      if (p[rowKey] !== next) return p;
      current = true;
      return { ...p, [rowKey]: settled };
    });
    if (current && onSettled) onSettled(settled);
  };

  /* AN EXISTING ROW IS CORRECTED ONE CELL AT A TIME TOO — Max, 2026-10-12, on two Chair One (re)
     rows where only the month was wrong: "có trường hợp các dòng sau đúng hết sai mỗi cái năm thui
     mà tui muốn chỉ cái năm thui thì lại bắt tui chọn lại từng cái á huhu". Unlocked, the row's five
     cells open holding what the row already is; change one and the others stay wherever they still
     fit. When the row's own Skin/Case is still made on the new choice, the row moves there by
     itself — that part was never in question, so asking for it again would be the retyping this
     replaces. Where it is not, the Skin/Case cell is left for the person to pick, as on a new row. */
  const rowBase = (e) => ({
    month: e.shipMonth || undefined, po: e.poLabel || undefined,
    description: e.description || undefined, colour: e.colour ?? '', part: e.part,
  });
  const repointWhenSettled = (e) => async (pk) => {
    const base = rowBase(e);
    if (!PICK_FIELDS.every((f) => pk[f] !== undefined)) return;          // a cell is still open
    if (PICK_FIELDS.every((f) => (pk[f] ?? '') === (base[f] ?? ''))) {    // back where it started
      clearPicks(`row-${e.id}`);
      return;
    }
    const parts = await fetchOptions('part', filtersFor(pk, '')).catch(() => []);
    const same = parts.find((o) => o.part === e.part);
    if (!same) return;
    if (await repoint(e, same)) clearPicks(`row-${e.id}`);
  };
  const clearPicks = (rowKey) => setPicks((p) => { const n = { ...p }; delete n[rowKey]; return n; });

  /* THE UNMATCHED ROWS OPEN FILLED IN — Max, 2026-10-12: "nếu thể hiện như vầy là tôi phải gõ lại từng
     cái á huhu". The server reads each row's own wording and says which item, colour, month and PO it
     points at (/unmatched/suggest); those go straight into the row's cells, tinted as a guess, and any
     cell left with a single answer fills itself. Seeded ONCE per row: Clear empties it and it stays
     empty, and a reload after attaching one row does not overwrite what was picked on the others. */
  const [guesses, setGuesses] = useState({});
  const seeded = useRef(new Set());
  const unKey = unmatchedRows.map((u) => u.id).join(',');
  useEffect(() => {
    if (!unKey || !vendorCode) return undefined;
    let dead = false;
    fetch(apiUrl('/unmatched/suggest', { date }), { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (!dead && d.suggestions) setGuesses(d.suggestions); })
      .catch(() => {});
    return () => { dead = true; };
  }, [unKey, date, vendorCode]);
  useEffect(() => {
    const fresh = unmatchedRows.filter((u) => guesses[u.id] && !seeded.current.has(u.id));
    if (!fresh.length) return;
    const seeds = {};
    fresh.forEach((u) => {
      seeded.current.add(u.id);
      const g = guesses[u.id];
      const pk = { _guess: {}, _loose: !!g.loose };
      PICK_FIELDS.forEach((f) => {
        if (g[f] !== undefined && g[f] !== null) { pk[f] = g[f]; pk._guess[f] = g[f]; }
      });
      seeds[`un-${u.id}`] = pk;
    });
    setPicks((p) => {
      const n = { ...p };
      Object.entries(seeds).forEach(([k, v]) => { if (!n[k]) n[k] = v; });
      return n;
    });
    Object.entries(seeds).forEach(async ([k, v]) => {
      if (PICK_FIELDS.every((f) => v[f] !== undefined)) return;
      const settled = await settlePicks(v, fetchOptions).catch(() => v);
      setPicks((p) => (p[k] === v ? { ...p, [k]: settled } : p));
    });
  }, [guesses, unmatchedRows, fetchOptions]);
  /* AND ITS SKIN/CASE TOO, SO A RIGHT GUESS IS ONE PRESS — Max, 2026-10-13: "hầu như bạn import đúng
     rùi và tôi chỉ vô chọn skin hay case lại thì nó bị mất thời gian á". The vendor's file already
     says which part the row is; once the four cells are set, the screen asks /options for the parts of
     that item — the same list the Skin/Case cell opens, so the line it lands on is the one picking it
     would have chosen — and, where the file's part is on it, fills the cell with it, tinted as a guess,
     and offers Attach. NOTHING IS WRITTEN UNTIL ATTACH IS PRESSED: a guess that sits in a cell is
     checkable, one that attached itself would be a row somebody has to find and undo. Looked up once
     per combination of the four (`_readyFor`); changing any of them drops the part with them. An
     "Accessory" row, or a part the item is not sewn in, is left for the person to choose. */
  const readyLooking = useRef(new Set());
  useEffect(() => {
    unmatchedRows.forEach((u) => {
      const key = `un-${u.id}`;
      const pk = picks[key];
      const part = String(u.part || '').trim().toLowerCase();
      if (!pk || (part !== 'skin' && part !== 'case')) return;
      if (!PICK_FIELDS.every((f) => pk[f] !== undefined)) return;
      const sig = PICK_FIELDS.map((f) => pk[f]).join('\u0001');
      if (pk._readyFor === sig || readyLooking.current.has(`${key}|${sig}`)) return;
      readyLooking.current.add(`${key}|${sig}`);
      fetchOptions('part', filtersFor(pk, ''))
        .then((opts) => opts.find((o) => String(o.part).toLowerCase() === part) || null)
        .catch(() => null)
        .then((hit) => {
          readyLooking.current.delete(`${key}|${sig}`);
          setPicks((p) => {
            const cur = p[key];
            if (!cur || PICK_FIELDS.map((f) => cur[f]).join('\u0001') !== sig) return p;
            return {
              ...p,
              [key]: {
                ...cur,
                part: hit ? hit.part : undefined,
                _ready: hit,
                _readyFor: sig,
                _guess: { ...(cur._guess || {}), part: hit ? hit.part : undefined },
              },
            };
          });
        });
    });
  }, [picks, unmatchedRows, fetchOptions]);

  const attachOne = async (u) => {
    const pk = picks[`un-${u.id}`];
    if (!pk || !pk._ready) return;
    if (await attachUnmatched(u, pk._ready)) clearPicks(`un-${u.id}`);
  };
  /* Every row on screen whose cells are all filled, in one go — one reload at the end rather than one
     per row. Asked first, because it writes several rows; the question carries how many go over their
     order, which on this data is the usual case rather than a slip (old output already sits there). */
  const [attaching, setAttaching] = useState(false);
  const attachAllReady = async (list) => {
    if (!list.length || attaching) return;
    const over = list.filter((u) => Number(u.qty || 0) > Number(picks[`un-${u.id}`]._ready.remainQty || 0)).length;
    const ask = t('xOutput.confirmAttachAll', { n: list.length })
      + (over ? `\n\n${t('xOutput.confirmAttachOver', { over })}` : '');
    if (!window.confirm(ask)) return;
    setError('');
    if (editing && dirtyCount) {
      const ok = await saveAll({ keepEditing: true });
      if (!ok) return;
    }
    setAttaching(true);
    const failed = [];
    const done = [];
    for (const u of list) {
      const r = picks[`un-${u.id}`]._ready;
      try {
        const res = await fetch(`${API}/unmatched/${u.id}/attach`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ capacityLineId: r.lineId, part: r.part }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        done.push(u.id);
      } catch (err) {
        failed.push(`${u.description || t('xOutput.rowWord')} (${u.part}): ${err.message}`);
      }
    }
    setPicks((p) => { const n = { ...p }; done.forEach((id) => delete n[`un-${id}`]); return n; });
    const wasEditing = editing;
    await load();
    loadSheets();
    if (wasEditing) setBulk(true);
    setAttaching(false);
    if (failed.length) setError(t('xOutput.attachSummary', { done: done.length, failed: failed.length, list: failed.join(' · ') }));
  };

  /* The five cells of a row being chosen. Frozen like the cells they stand in for. */
  const comboCells = (rowKey, hint, finish, base, onSettled) => {
    const pk = picks[rowKey] || base || {};
    const ready = ['month', 'po', 'description', 'colour'].every((f) => pk[f] !== undefined);
    const cell = (field, i, extra, ph, wide) => (
      <td {...frozenProps(i, `${extra || ''} co-combocell`.trim())}>
        <CellCombo
          field={field}
          value={pk[field]}
          filters={field === 'part' ? filtersFor(pk, '') : filtersFor(pk, field)}
          fetchOptions={fetchOptions}
          onPick={(v, how) => pickCell(rowKey, field, v, finish, base, onSettled, how)}
          widerFilters={field === 'part' ? null : Object.fromEntries(RELAX[field]
            .filter((f) => pk[f] !== undefined && pk[f] !== '').map((f) => [f, pk[f]]))}
          placeholder={ph}
          wide={wide}
          guess={pk[field] !== undefined && pk._guess && pk._guess[field] === pk[field]
            ? (field === 'description' && pk._loose ? 'loose' : 'guess') : undefined}
          prefer={field === 'part' ? hint.part : undefined}
          detail={[
            ...(hint.words && hint.words[field] ? [['file', t('xOutput.inTheFile'), hint.words[field]]] : []),
            ...(field === 'po' && hint.why ? [['why', t('xOutput.notMatched'), hint.why]] : []),
          ]}
          disabled={field === 'part' && !ready}
          title={field === 'part' && !ready ? t('xOutput.chooseFirst') : ''}
        />
        {/* Once the cells are filled the vendor's words are no longer the placeholder, and the guess
            is only checkable against them — so they stay under the item. The reason the import could
            not place the row stays too, under the PO (an order that is full means choosing another). */}
        {field === 'po' && hint.why && (
          <div className="co-unhint" title={[hint.why, hint.po && t('xOutput.filePo', { po: hint.po })].filter(Boolean).join(' · ')}>{hint.why}</div>
        )}
        {field === 'description' && hint.file && <div className="co-unhint" title={hint.file}>{t('xOutput.fileLabel', { file: hint.file })}</div>}
      </td>
    );
    return (
      <>
        {cell('month', 1, '', hint.month || t('xOutput.colMonth'))}
        {cell('po', 2, 'co-po', hint.po || t('xOutput.colPo'), true)}
        {cell('description', 3, 'co-what', hint.description || t('xOutput.searchDescription'), true)}
        {cell('colour', 4, '', hint.colour || t('xOutput.colColour'))}
        {cell('part', 5, '', hint.part || t('xOutput.colSkinCase'))}
      </>
    );
  };

  /* For a row that cannot go on any order — an Accessory, a sample run. It leaves the sheet and the
     Unmatched list; nothing is imported or deleted, and who decided is recorded. */
  const dismissUnmatched = async (u) => {
    setError('');
    try {
      const res = await fetch(`${API}/unmatched/${u.id}/resolve`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xOutput.couldNotMark', { status: res.status }));
      setUnmatchedRows((prev) => prev.filter((x) => x.id !== u.id));
      loadSheets();
    } catch (err) { setError(err.message); }
  };

  /* One box, one behaviour now: while the table is unlocked it remembers, and Save writes the lot.
     The old "write when you leave the box" path is gone with the lock — two ways to save one figure
     is how a row ended up half written by a blur and half by a Save. */
  const cellProps = (e, key) => (editing
    ? {
      value: metaValue(e, key),
      onChange: (ev) => setMetaValue(e.id, key, ev.target.value),
    }
    : {
      value: e[key] === null || e[key] === undefined ? '' : String(e[key]),
      readOnly: true,
      onChange: () => {},
    });

  /* THE WHOLE TABLE, IN ONE REQUEST. The server writes it in one transaction, so a day is either
     what the screen shows or untouched — twenty boxes were twenty requests, each free to fail on its
     own and leave half a day saved with nothing saying which half. */
  const saveAll = async ({ keepEditing = false } = {}) => {
    const ids = [...dirtyIds()];
    if (!ids.length) { if (!keepEditing) setBulk(false); return true; }
    const payload = ids.map((id) => {
      const row = { id };
      META_COLS.forEach((key) => {
        const k = `${id}|${key}`;
        if (k in metaDraft) row[key] = metaDraft[k];
      });
      if (slots.some((sl) => `${id}|${sl}` in draft)) row.hours = liveHours(id);
      return row;
    }).filter((row) => Object.keys(row).length > 1);
    setSavingAll(true); setError('');
    try {
      const res = await fetch(`${API}/bulk`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: payload }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xOutput.saveFailed', { status: res.status }));
      const byId = new Map((data.entries || []).map((x) => [x.id, x]));
      setEntries((prev) => prev.map((x) => (byId.has(x.id) ? { ...x, ...byId.get(x.id) } : x)));
      draftRef.current = {};
      setDraft({}); setMetaDraft({});
      if (!keepEditing) setBulk(false);
      loadSheets();
      return true;
    } catch (err) {
      // Nothing was written — the transaction rolled back — so the typing stays on screen to retry.
      setError(err.message);
      return false;
    } finally {
      setSavingAll(false);
    }
  };

  /* Changing the day or reloading throws the table away. Asked once, in one place, so no path out of
     an unsaved table is missing it. */
  const leaveDay = () => {
    if (!bulk || !dirtyCount) return true;
    // eslint-disable-next-line no-alert
    return window.confirm(t('xOutput.confirmLeave', { n: dirtyCount }));
  };

  const cancelAll = () => {
    // eslint-disable-next-line no-alert
    if (dirtyCount && !window.confirm(t('xOutput.confirmDiscard', { n: dirtyCount }))) return;
    draftRef.current = {};
    setDraft({}); setMetaDraft({}); setBulk(false);
  };

  const removeRow = async (e) => {
    if (!window.confirm(t('xOutput.confirmRemove', { what: e.description || e.sapCode, part: e.part, date }))) return;
    try {
      const res = await fetch(`${API}/${e.id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xOutput.couldNotRemove', { status: res.status }));
      setEntries((prev) => prev.filter((x) => x.id !== e.id));
    } catch (err) {
      setError(err.message);
    }
  };

  /* Left and Right moved between boxes only when the caret was at the end of the text — and on an
     <input type="number"> Chrome REFUSES to report the caret: reading selectionStart throws, and the
     comparison `null === 2` is simply false, so the arrows did nothing at all on every numeric box on
     the screen. Max, 2026-09-12: "ko di chuyển ô qua trái phải bằng nút trên máy tính được".
     Asking is now guarded, and a box that cannot answer is treated as ready to move — which is also
     what a spreadsheet does: the arrows walk the grid, they do not walk the digits. */
  const caretAt = (el, atEnd) => {
    try {
      if (el.selectionStart === null) return true;
      return atEnd ? el.selectionStart === el.value.length : el.selectionStart === 0;
    } catch (err) {
      return true;
    }
  };
  const gridKeys = (rowIdx, colIdx) => (ev) => {
    const move = (dr, dc) => {
      const el = gridRef.current?.querySelector(`[data-cell="${rowIdx + dr}-${colIdx + dc}"]`);
      if (el) { ev.preventDefault(); el.focus(); el.select?.(); }
    };
    if (ev.key === 'Enter' || ev.key === 'ArrowDown') move(1, 0);
    else if (ev.key === 'ArrowUp') move(-1, 0);
    else if (ev.key === 'ArrowRight' && caretAt(ev.target, true)) move(0, 1);
    else if (ev.key === 'ArrowLeft' && caretAt(ev.target, false)) move(0, -1);
  };

  /* What the screen is showing, in the order it is showing it. Sorting and filtering are the grid's,
     not the server's — a day is at most a few dozen rows. */
  const shown = gridMenu.apply(entries);
  const shownUn = gridMenu.apply(unGrid);
  // On screen, unlocked, and every cell filled — what Attach all would write.
  const readyUn = editing ? shownUn.filter((u) => picks[`un-${u.id}`] && picks[`un-${u.id}`]._ready) : [];
  const unQty = shownUn.reduce((a, u) => a + (u.qty || 0), 0);

  const totals = shown.reduce((a, e) => {
    const pending = Object.keys(draft).some((k) => k.startsWith(`${e.id}|`));
    const g = rowGap(e, hoursOf(e), pending);
    return {
      qty: a.qty + dayTotal(e, hoursOf(e)),
      hours: a.hours + (workingHours(e) || 0),
      rows: a.rows + 1,
      /* Counted separately and shown separately: a figure that is not in the total has to be
         visible somewhere, or removing it from the total is just losing it. */
      gapRows: a.gapRows + (g ? 1 : 0),
      gapQty: a.gapQty + (g ? g.stored : 0),
      gapOpening: a.gapOpening + (g && g.opening ? 1 : 0),
    };
  }, { qty: 0, hours: 0, rows: 0, gapRows: 0, gapQty: 0, gapOpening: 0 });

  /* ---- pasting a block out of Excel ---------------------------------------------------------
     Max, 2026-09-12: "copy 3 cột và 2 dòng vào ô cũng ko dc nó cứ gôm lại 1 ô thui". A paste into an
     <input> is a paste into ONE input — the tabs and newlines go in as text and a number box keeps the
     first number it can read. So the block is taken apart here and laid across the grid from the box
     that was focused, exactly as a spreadsheet would. Anything past the last row or the last column is
     dropped rather than inventing rows: a row here is an ORDER, and orders are chosen, never typed. */
  const onPasteGrid = async (ev) => {  // eslint-disable-line consistent-return
    if (!mayEdit) return;
    const cell = ev.target?.dataset?.cell;
    if (!cell) return;
    const text = ev.clipboardData?.getData('text/plain') || '';
    const block = text.replace(/\r\n?/g, '\n').replace(/\n+$/, '').split('\n').map((r) => r.split('\t'));
    // One value stays a normal paste: the browser already does that better than this can.
    if (block.length <= 1 && block[0].length <= 1) return;
    ev.preventDefault();
    const [r0, c0] = cell.split('-').map(Number);
    const hourDraft = {};
    const plan = [];
    block.forEach((cells, dr) => {
      const e = shown[r0 + dr];
      if (!e) return;
      const patch = {};
      let hours = false;
      cells.forEach((raw, dc) => {
        const c = c0 + dc;
        const value = String(raw).trim();
        if (c < META_COLS.length) {
          const key = META_COLS[c];
          // Excel hands over "1,234" and "12.0 " — a number box would silently keep the 1.
          patch[key] = key === 'lineNo' ? value : cleanNumber(value);
        } else {
          const slot = slots[c - META_COLS.length];
          if (slot) { hourDraft[`${e.id}|${slot}`] = cleanNumber(value); hours = true; }
        }
      });
      if (Object.keys(patch).length || hours) plan.push({ e, patch, hours });
    });
    if (!plan.length) return;
    /* The ref is written here as well as the state: it is synced by an effect that has not run yet,
       and saveHours reads the REF — so the first row would otherwise save the values it had before
       the paste. */
    draftRef.current = { ...draftRef.current, ...hourDraft };
    setDraft((prev) => ({ ...prev, ...hourDraft }));
    /* Inside "Sửa cả bảng" a paste fills the boxes and waits for the one Save, like typing does. */
    if (bulk) {
      const next = {};
      plan.forEach(({ e, patch }) => Object.entries(patch)
        .forEach(([key, value]) => { next[`${e.id}|${key}`] = value; }));
      setMetaDraft((prev) => ({ ...prev, ...next }));
      return;
    }
    // One row at a time. Two writes racing on one row is what once left 98 on screen and 46 in the
    // database, and a pasted block is exactly the shape that fires them all at once.
    // eslint-disable-next-line no-restricted-syntax
    for (const step of plan) {
      // eslint-disable-next-line no-await-in-loop
      if (Object.keys(step.patch).length) await save(step.e, step.patch);
      // eslint-disable-next-line no-await-in-loop
      if (step.hours) await saveHours(step.e.id);
    }
  };

  const colgroup = (
    <colgroup>
      {FIXED.map(([k, w]) => <col key={k} style={{ width: w }} />)}
      {slots.map((sl) => <col key={sl} style={{ width: SLOT_W }} />)}
      {TAIL.map(([k, w]) => <col key={k} style={{ width: w }} />)}
    </colgroup>
  );

  return (
    <div className={`sp-page cap-page co-page${bulk ? ' co-bulk' : ''}`}>
      <Header />
      <div className="sp-main">
        <div className="mh-titlerow">
          <div className="cap-head">
            <h2>{t('xOutput.title')}</h2>
            <p>{t('xOutput.intro')}</p>
          </div>
          <ModuleHelp steps={TOUR} tourKey="dailyOutput" ready={!loading} />
        </div>

        {!!error && <div className="sp-error"><AlertTriangle size={14} /> {error}</div>}

        {!locked && (
          <div className="cap-bar co-vendorbar">
            <span>{t('xOutput.colVendor')}</span>
            <select value={pickVendor} onChange={(ev) => setPickVendor(ev.target.value)}>
              <option value="">{t('xOutput.chooseVendor')}</option>
              {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        )}

        {!vendorCode ? (
          <div className="co-novendor">
            {locked ? t('xOutput.notLinked') : t('xOutput.chooseVendorToSee')}
          </div>
        ) : (
          <>
            <div className="cap-bar co-daybar">
              <span className="co-datewrap">
                <CalendarDays size={13} />
                <input
                  type="date"
                  value={date}
                  onChange={(ev) => { if (leaveDay()) setDate(ev.target.value); }}
                />
              </span>
              <button
                type="button"
                className="sp-btn"
                onClick={() => { if (leaveDay()) setDate(today()); }}
                disabled={date === today()}
              >
                {t('xOutput.todayBtn')}
              </button>
              <button
                type="button"
                className={`sp-btn ${showCal ? 'cap-on' : ''}`}
                title={t('xOutput.sheetsBtnTitle')}
                onClick={() => setShowCal((v) => !v)}
              >
                <ClipboardList size={13} /> {t('xOutput.sheetsTitle')}
              </button>
              <button
                type="button"
                className={`sp-btn ${showCheck ? 'cap-on' : ''}`}
                title={t('xOutput.checkBtnTitle')}
                onClick={() => setShowCheck((v) => !v)}
              >
                <ShieldCheck size={13} /> {t('xOutput.checkBtn')}
              </button>
              <span className="co-sum">
                <strong>{n0(totals.qty) || '0'}</strong> {t('xOutput.colPcs')}
                <em>
                  {t('xOutput.hourBoxesAdded')} · {t('xOutput.rowsN', { count: totals.rows })}
                  {gridMenu.filtered ? ` / ${entries.length}` : ''} · {t('xOutput.workHoursN', { n: n0(totals.hours) || '0' })}
                </em>
              </span>
              {/* A filtered grid shows a filtered total. Said out loud, or the number reads as the
                  day's when it is only part of it. */}
              {gridMenu.active && (
                <button type="button" className="sp-btn co-clearfilter" onClick={gridMenu.clear}>
                  <X size={12} /> {gridMenu.filtered ? t('xOutput.filteredClear') : t('xOutput.sortedClear')}
                </button>
              )}
              {shownUn.length > 0 && (
                <span
                  className="co-unnote"
                  title={t('xOutput.unNoteTitle')}
                >
                  <TriangleAlert size={13} />
                  {t('xOutput.unNoteText', { n: shownUn.length, qty: n0(unQty) })}
                </span>
              )}
              {readyUn.length > 0 && (
                <button
                  type="button"
                  className="sp-btn co-attachall"
                  disabled={attaching}
                  title={t('xOutput.attachAllTitle')}
                  onClick={() => attachAllReady(readyUn)}
                >
                  <Link2 size={12} /> {attaching ? t('xOutput.attaching') : t('xOutput.attachFilled', { count: readyUn.length })}
                </button>
              )}
              {/* EARLIER DAYS, NOT JUST THIS ONE — Max, 2026-09-13: "sau ko có cảnh báo cho những ngày
                  trước vậy tôi thấy nhiều cái chưa khớp cần chỉnh lại mà". The screen only ever warned
                  about the day on screen, so a mismatch typed last Tuesday sat there until somebody
                  happened to open Tuesday. This counts every earlier day the vendor has filled in and
                  opens the list already filtered to them. */}
              {needCheck.earlier.length > 0 && (
                <button
                  type="button"
                  className="co-daywarn"
                  title={needCheck.earlier.length > 12
                    ? t('xOutput.earlierMore', {
                      dates: needCheck.earlier.slice(0, 12).map((d) => d.date).join(', '),
                      n: needCheck.earlier.length - 12,
                    })
                    : needCheck.earlier.map((d) => d.date).join(', ')}
                  onClick={() => { setIssuesOnly(true); setShowCal(true); }}
                >
                  <TriangleAlert size={13} />
                  {t('xOutput.earlierDays', { count: needCheck.earlier.length })}
                </button>
              )}

              {/* Not in the total, and said so — the note Max asked for so these can be checked later. */}
              {totals.gapRows > 0 && (
                <span
                  className="co-gapnote"
                  title={t('xOutput.gapNoteTitle')}
                >
                  <TriangleAlert size={13} />
                  {t('xOutput.gapNote', { count: totals.gapRows, qty: n0(totals.gapQty) })}
                  {totals.gapOpening === totals.gapRows ? ` ${t('xOutput.openingParen')}` : ''}
                </span>
              )}
              <div className="cap-bar-right">
                {mayEdit && !bulk && (
                  <button
                    type="button"
                    className="sp-btn"
                    title={t('xOutput.editTableTitle')}
                    onClick={() => setBulk(true)}
                  >
                    <Pencil size={13} /> {t('xOutput.editTable')}
                  </button>
                )}
                {bulk && (
                  <>
                    <button
                      type="button"
                      className="sp-btn primary co-saveall"
                      onClick={saveAll}
                      disabled={savingAll}
                    >
                      <Check size={13} />
                      {savingAll ? t('xOutput.saving')
                        : (dirtyCount ? t('xOutput.saveTableN', { count: dirtyCount }) : t('xOutput.saveTable'))}
                    </button>
                    <button type="button" className="sp-btn" onClick={cancelAll} disabled={savingAll}>
                      <X size={13} /> {t('xOutput.cancel')}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className={`sp-btn ${showUnmatched ? 'cap-on' : ''}`}
                  title={t('xOutput.unmatchedBtnTitle')}
                  onClick={() => setShowUnmatched((v) => !v)}
                >
                  <TriangleAlert size={13} /> {t('xOutput.unmatchedBtn')}
                </button>
                <button
                  type="button"
                  className="sp-btn"
                  onClick={() => { if (leaveDay()) load(); }}
                  disabled={loading || savingAll}
                >
                  <RefreshCw size={13} /> {loading ? t('xOutput.loading') : t('xOutput.reload')}
                </button>
              </div>
            </div>

            {showCheck && (
              <OutputCheck api={API} vendor={vendorCode} onPickDay={(d) => { if (leaveDay()) setDate(d); }} />
            )}

            {showCal && (
              <SheetList
                current={date}
                today={today()}
                onPickDay={setDate}
                data={sheets}
                err={sheetsErr}
                busy={sheetsBusy}
                onReload={loadSheets}
                issuesOnly={issuesOnly}
                onIssuesOnly={setIssuesOnly}
              />
            )}

            {showUnmatched && (
              <Unmatched canResolve={mayEdit} onClose={() => setShowUnmatched(false)} />
            )}

            <datalist id="co-lines">
              {lineNames.map((l) => <option key={l} value={l}>{l}</option>)}
            </datalist>

            {bulk && (
              <div className="co-bulkhint">
                <Pencil size={13} />
                <span>
                  {/* <b>…</b> in the dictionary is the button's name, drawn bold. */}
                  <Trans i18nKey="xOutput.bulkHint" components={{ b: <strong /> }} />
                </span>
              </div>
            )}
            <div className={`cap-scroll${bulk ? ' co-bulk-on' : ''}`} ref={gridRef} onPaste={onPasteGrid}>
              <table
                className="cap-table co-table"
                style={{ width: FIXED.reduce((a, [, w]) => a + w, 0)
                  + slots.length * SLOT_W + TAIL.reduce((a, [, w]) => a + w, 0) }}
              >
                {colgroup}
                <thead>
                  <tr>
                    <th className="co-rowno" {...frozenProps(0)}>#</th>
                    {/* Funnels on the columns whose values repeat — which is what a filter is for. A
                        tick list of forty different quantities answers nothing, so Order, Workers,
                        Hrs, Total, Remain and Eff. stay plain. */}
                    <SortTh label={t('xOutput.colMonth')} {...gridTh('shipMonth')} {...frozenProps(1)} />
                    <SortTh label={t('xOutput.colPo')} {...gridTh('poLabel')} {...frozenProps(2, 'co-po')} />
                    <SortTh label={t('xOutput.colDescription')} {...gridTh('description')} {...frozenProps(3, 'co-what')} />
                    <SortTh label={t('xOutput.colColour')} {...gridTh('colour')} {...frozenProps(4)} />
                    <SortTh label={t('xOutput.colSkinCase')} {...gridTh('part')} {...frozenProps(5)} />
                    <th className="cap-num">{t('xOutput.colOrder')}</th>
                    <SortTh label={t('xOutput.colLine')} {...gridTh('lineNo')} />
                    <th className="cap-num">{t('xOutput.colWorkers')}</th>
                    <th className="cap-num">{t('xOutput.colHrs')}</th>
                    {slots.map((s) => <th key={s} className="cap-num co-slot">{s}</th>)}
                    <th className="cap-num co-total">{t('xOutput.colTotal')}</th>
                    <th className="cap-num">{t('xOutput.colRemain')}</th>
                    <th className="cap-num">{t('xOutput.colEff')}</th>
                    <th className="cap-act">·</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((e, ri) => {
                    const hours = hoursOf(e);
                    const total = dayTotal(e, hours);
                    const eff = efficiency(e, total);
                    const busy = savingIds.includes(e.id);
                    const pending = Object.keys(draft).some((k) => k.startsWith(`${e.id}|`));
                    const gap = rowGap(e, hours, pending);
                    return (
                      <tr key={e.id} className={`${busy ? 'co-saving' : ''} ${gap ? 'co-gaprow' : ''}`}>
                        <td {...frozenProps(0, 'co-rowno')}>{ri + 1}</td>
                        {editing && mayEdit ? comboCells(`row-${e.id}`, {
                          // part is the row's own, so its Skin/Case list opens with it lit.
                          month: t('xOutput.colMonth'), po: t('xOutput.colPo'), description: t('xOutput.searchDescription'), colour: t('xOutput.colColour'), part: e.part,
                        }, (chosen) => repoint(e, chosen), rowBase(e), repointWhenSettled(e)) : (
                        <>
                        <td {...frozenProps(1, 'co-dim')}>{e.shipMonth}</td>
                        <td title={e.poLabel || ''} {...frozenProps(2, 'co-po co-dim')}>{e.poLabel}</td>
                        <td {...frozenProps(3, 'co-what co-pickcell')}>
                          {mayEdit ? (
                            <button
                              type="button"
                              className="co-editbtn"
                              title={t('xOutput.pickAgainTitle')}
                              onClick={(ev) => setPickingAt({
                                row: e.id, anchor: ev.currentTarget.getBoundingClientRect(),
                              })}
                            >
                              {e.description || e.sapCode}
                            </button>
                          ) : (e.description || e.sapCode)}
                          {pickingAt?.row === e.id && (
                            <Cascade
                              seed={{ month: e.shipMonth, po: e.poLabel }}
                              anchor={pickingAt.anchor}
                              onPick={(part) => repoint(e, part)}
                              onClose={() => setPickingAt(null)}
                            />
                          )}
                        </td>
                        <td title={e.colour || ''} {...frozenProps(4, 'co-dim')}>{e.colour}</td>
                        <td {...frozenProps(5, 'co-dim')}>{e.part}</td>
                        </>
                        )}
                        <td className="cap-num co-dim">{n0(e.orderQty)}</td>
                        <td className="co-cell">
                          <input
                            list="co-lines"
                            key={`ln-${e.id}-${editing ? 'b' : e.lineNo ?? ''}`}
                            data-cell={`${ri}-0`}
                            disabled={!mayEdit}
                            onKeyDown={gridKeys(ri, 0)}
                            {...cellProps(e, 'lineNo')}
                          />
                        </td>
                        <td className="cap-num co-cell">
                          <input
                            type="number"
                            key={`wk-${e.id}-${editing ? 'b' : e.workers ?? ''}`}
                            data-cell={`${ri}-1`}
                            disabled={!mayEdit}
                            onKeyDown={gridKeys(ri, 1)}
                            {...cellProps(e, 'workers')}
                          />
                        </td>
                        <td className="cap-num co-cell">
                          <input
                            type="number"
                            step="any"
                            key={`wh-${e.id}-${editing ? 'b' : e.workingHours ?? ''}`}
                            data-cell={`${ri}-2`}
                            disabled={!mayEdit}
                            onKeyDown={gridKeys(ri, 2)}
                            {...cellProps(e, 'workingHours')}
                          />
                        </td>
                        {slots.map((s, ci) => (
                          /* co-slotcell says "this box is an HOUR" — the line, workers and hours
                             boxes are grid cells too now, and a check that counted every box in the
                             row typed a production line into one of them. */
                          <td key={s} className="cap-num co-cell co-slotcell">
                            <input
                              type="number"
                              data-cell={`${ri}-${ci + META_COLS.length}`}
                              value={valueOf(e, s)}
                              disabled={!mayEdit}
                              readOnly={!editing}
                              onChange={(ev) => {
                                if (!editing) return;
                                /* Typing the first hour on a flagged row REPLACES its stored total —
                                   which is the point, it is how the row gets fixed — but 184 becoming
                                   30 because somebody typed one hour and walked away is a loss nobody
                                   notices until the month is counted. So it is said out loud first. */
                                if (!e.hoursEntered && Number(e.dayQty) > 0
                                    && !Object.keys(draft).some((k) => k.startsWith(`${e.id}|`))
                                    // eslint-disable-next-line no-alert
                                    && !window.confirm(t('xOutput.confirmTypeHours', { qty: n0(e.dayQty) }))) return;
                                setDraft((p) => ({ ...p, [`${e.id}|${s}`]: ev.target.value }));
                              }}
                              onKeyDown={gridKeys(ri, ci + META_COLS.length)}
                              /* Saved on blur with the WHOLE slot map: a cleared box has to mean
                                 "nothing made in that hour", which a patch cannot express. */
                              /* Nothing is written on blur any more — the table saves in one press.
                                 Kept as a no-op rather than deleted so the next person looking for
                                 the old autosave finds the reason it went. */
                            />
                          </td>
                        ))}
                        {/* Read-only on purpose. The total is what the hours say; there is no box
                            here to type a different answer into. */}
                        <td className={`cap-num co-total ${gap ? 'co-gap-cell' : ''}`} title={gap ? gapText(gap, t) : ''}>
                          {n0(total) || '0'}
                          {gap && (
                            <span className="co-gapmark">
                              <TriangleAlert size={11} />{gap.opening ? `${t('xOutput.opShort')} ` : ''}{n0(gap.stored)}
                            </span>
                          )}
                        </td>
                        <td className="cap-num co-dim">{n0(e.remainQty)}</td>
                        <td className={`cap-num ${effTone(eff)}`}>{eff === null ? '' : `${n1(eff)}%`}</td>
                        <td className="cap-act">
                          {busy ? <Check size={12} className="co-tick" /> : mayEdit && (
                            <button type="button" className="sp-btn tiny" title={t('xOutput.removeRowTitle')} onClick={() => removeRow(e)}>
                              <Trash2 size={12} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {/* THE REST OF THE DAY — what the factory's file says was made on this day but the
                      import could not attach to an order. Its own colour, the reason under the item,
                      the hours as the file had them; never editable in place and never in the total.
                      Unlocked, each one can be attached to an order (the same picker as a blank row)
                      or marked dealt with. */}
                  {shownUn.map((u, ui) => (
                    <tr key={`un-${u.id}`} className="co-unrow" title={u.reason || ''}>
                      <td {...frozenProps(0, 'co-rowno')}>{shown.length + ui + 1}</td>
                      {editing ? comboCells(`un-${u.id}`, {
                        // The vendor's own words as the placeholders: what to search for.
                        po: u.poText, description: u.description, colour: u.colour,
                        part: u.part, why: u.reason,
                        // The vendor's own words per cell, shown whole when the cell is opened.
                        words: { po: u.poText, description: u.description, colour: u.colour, part: u.part },
                        file: [u.description, u.colour, u.part].filter(Boolean).join(' · '),
                      }, (chosen) => attachUnmatched(u, chosen)) : (
                        <>
                          <td {...frozenProps(1, 'co-untagcell')}><span className="co-untag">{t('xOutput.notMatched')}</span></td>
                          <td title={u.poText || ''} {...frozenProps(2, 'co-po co-dim')}>{u.poText}</td>
                          <td {...frozenProps(3, 'co-what co-pickcell')}>
                            <div className="co-unwhat" title={u.description || ''}>{u.description}</div>
                            <div className="co-unwhy" title={u.reason || ''}>{u.reason}</div>
                          </td>
                          <td title={u.colour || ''} {...frozenProps(4, 'co-dim')}>{u.colour}</td>
                          <td {...frozenProps(5, 'co-dim')}>{u.part || '—'}</td>
                        </>
                      )}
                      <td className="cap-num co-dim" />
                      <td className="co-dim">{u.lineNo}</td>
                      <td className="cap-num co-dim">{u.workers ?? ''}</td>
                      <td className="cap-num co-dim">{u.workingHours ?? ''}</td>
                      {slots.map((sl) => (
                        <td key={sl} className="cap-num co-dim">{u.hours?.[sl] ? n0(u.hours[sl]) : ''}</td>
                      ))}
                      <td className="cap-num co-total"><strong>{n0(u.qty) || '0'}</strong></td>
                      <td className="cap-num" />
                      <td className="cap-num" />
                      <td className="cap-act co-unact">
                        {editing && (() => {
                          const pk = picks[`un-${u.id}`];
                          const ready = pk && pk._ready;
                          const over = ready && Number(u.qty || 0) > Number(ready.remainQty || 0);
                          return (
                            <>
                              {ready && (
                                <button
                                  type="button"
                                  className={`sp-btn tiny co-attach${over ? ' co-attach-over' : ''}`}
                                  disabled={attaching}
                                  title={t('xOutput.attachTitle', {
                                    qty: n0(u.qty), po: pk.po, description: pk.description,
                                    colour: pk.colour || t('xOutput.blank'), part: ready.part,
                                  })
                                    + (over ? `\n${t('xOutput.attachOverTitle', { left: n0(ready.remainQty), order: n0(ready.orderQty) })}` : '')}
                                  onClick={() => attachOne(u)}
                                >
                                  <Link2 size={11} /> {t('xOutput.attach')}
                                </button>
                              )}
                              {!!pk && (
                                <button type="button" className="sp-btn tiny" title={t('xOutput.clearChosenTitle')} onClick={() => clearPicks(`un-${u.id}`)}>
                                  <X size={12} />
                                </button>
                              )}
                              {/* Not a tick: beside Attach a tick reads as "yes, that one", and this does the
                                  opposite — the row is put aside and NOTHING is recorded (Max pressed it for
                                  "confirm" eight times on 2026-10-12). */}
                              <button
                                type="button"
                                className="sp-btn tiny"
                                title={t('xOutput.dismissTitle')}
                                onClick={() => dismissUnmatched(u)}
                              >
                                <Ban size={12} />
                              </button>
                            </>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}

                  {/* The blank rows. They are the whole point of the screen opening as a grid: there is
                      no button to find, you type on the next empty line the way you would in Excel. */}
                  {/* With a filter on, one spare line is enough — fifteen empty rows under three
                      filtered ones read as a mistake. */}
                  {/* Only while the table is unlocked — a locked table is for reading, and that is the
                      whole of Max's rule. Picking an item still writes the row straight away (it has to:
                      the server issues the row), so anything typed is saved first — see pickInto. */}
                  {editing && Array.from(
                    { length: gridMenu.active ? 1 : blankCount(shown.length) }, (_, bi) => (
                    <tr key={`blank-${bi}`} className="co-blank">
                      <td {...frozenProps(0, 'co-rowno')}>{shown.length + shownUn.length + bi + 1}</td>
                      {comboCells(`blank-${bi}`, {}, (chosen) => pickInto(bi, chosen))}
                      <td colSpan={slots.length + 8}>
                        {!!picks[`blank-${bi}`] && (
                          <button type="button" className="sp-btn tiny co-blankclear" title={t('xOutput.clearChosenTitle')} onClick={() => clearPicks(`blank-${bi}`)}>
                            <X size={11} /> {t('xOutput.clearLower')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ),
                  )}

                  {!entries.length && !unGrid.length && !mayEdit && !loading && (
                    <tr>
                      <td colSpan={slots.length + 12} className="cap-empty">
                        {t('xOutput.nothingEntered', { date })}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
